import { useEffect, useRef, useState } from 'react';
import { createDeferredSave, type DeferredSave } from './deferred-save';

/**
 * A deferred save (deferred-save.ts) for a screen: anything waiting is written
 * when the app is hidden or closed, and when the screen goes. Always calls the
 * latest `save`.
 *
 * Closing or reloading a tab gives a save no time to finish (it reads the
 * stored record, then writes it; found on the test site 2026-10-20), so while
 * one is waiting or under way the browser asks "Leave site?": Stay keeps the
 * typing, Leave loses the last moment of it (Chrome pauses the page while it
 * asks, so the question can't buy the save time). Minimising the iPad doesn't
 * unload the page, so it never asks there (OFFLINE.md).
 */
export function useDeferredSave<T>(save: (value: T) => void | Promise<void>, combine?: (older: T, newer: T) => T): DeferredSave<T> {
  const latestSave = useRef(save);
  latestSave.current = save;
  const [saver] = useState(() => createDeferredSave<T>((value) => latestSave.current(value), combine));

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') void saver.flush();
    };
    const onPageHide = () => void saver.flush();
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      void saver.flush();
      if (saver.pending()) event.preventDefault();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeunload', onBeforeUnload);
      void saver.flush();
    };
  }, [saver]);

  return saver;
}
